import React from 'react'
import { Box, Flex, Text } from '@chakra-ui/core'
import { css } from '@emotion/core'

import LocaleText from 'components/LocaleText'
import { HoverPopover } from 'components/ui'
import { TASK_STATUS_CONFIG } from 'utils/taskAnalytics'
import {
  getSubtaskStatusBreakdown,
  isTerminalTaskStatus,
  SubtaskStatusCounts,
  SubtaskStatusKey,
  SUBTASK_STATUS_LOCALE_KEY,
  TaskStatusId,
  TASK_STATUS_LOCALE_KEY,
} from 'utils/taskStatusModel'
import { withAlpha } from 'utils'

import {
  CAPTION_FONT_SIZE,
  computeSegmentWidths,
  POPOVER_BAR_WIDTH,
  SEGMENT_GAP,
  StatusCompositionVariant,
  STATUS_COMPOSITION_TOKENS,
} from './utils'

/**
 * The caption is a fixed label with no count in it.
 *
 * It used to read `N outcomes`, except for rejections, which were named outright as `N Rejected` — but
 * the status badge right next to the block already says "Rejected", so that case read as the word
 * twice over. Numbers belong in the card the block opens, not in its trigger.
 *
 * A plain literal, not a locale key: no `summary` translation exists and none is to be added (the
 * analytics dashboard's `useLocaleText('summary')` also just falls through to this same string).
 */
const SUMMARY_LITERAL = 'summary'

type Bucket = { key: SubtaskStatusKey; count: number }

/**
 * One segment per subtask outcome, widths proportional to the counts. Segment order is the caller's
 * (fixed semantic order — see `SUBTASK_STATUS_ORDER`), never sorted by size, so rows stay
 * comparable down a list. In RTL the flex row mirrors itself — the array is never reversed by hand.
 */
const CompositionBar: React.FC<{ buckets: Bucket[]; total: number; width: number; height: number }> = ({
  buckets,
  total,
  width,
  height,
}) => {
  const widths = computeSegmentWidths(
    buckets.map((bucket) => bucket.count),
    total,
    width
  )

  return (
    <Flex
      w={`${width}px`}
      h={`${height}px`}
      borderRadius="999px"
      overflow="hidden"
      flexShrink={0}
      style={{ gap: `${SEGMENT_GAP}px` }}
      aria-hidden="true"
    >
      {buckets.map((bucket, index) => (
        <Box
          key={bucket.key}
          w={`${widths[index]}px`}
          h="100%"
          borderRadius="999px"
          flexShrink={0}
          bg={TASK_STATUS_CONFIG[bucket.key].bar}
        />
      ))}
    </Flex>
  )
}

interface IProps {
  /** The task/context status, already derived — see `utils/taskStatusModel`. */
  taskStatus: TaskStatusId
  /** Per-subtask status counts behind `taskStatus`. */
  counts: SubtaskStatusCounts
  variant: StatusCompositionVariant
}

/**
 * Shows WHAT a terminal task status is made of: a caption plus a mini bar of the subtask outcomes,
 * opening a card with the exact per-outcome numbers on click.
 *
 * Opens for the three terminal statuses only — `done`, `approved`, `rejected` (`isTerminalTaskStatus`);
 * for `todo` / `inProgress` the row's own progress bar already answers the question.
 *
 * Renders `null` when there is nothing to explain: a single outcome that repeats the badge (4/4 approved
 * under "Approved"). A single outcome with a different name is kept — that is how "Approved" explains
 * itself as approved-with-reservation or reviewed-by-AI, and "Done" as not-applicable (skipped).
 *
 * **Copy uses existing locale keys only** (`task_status_*`, rendered through `LocaleText`) plus the
 * fixed `summary` caption — no new translations. The caption carries no numbers and no status name of
 * its own: the badge beside it already names the status, and the counts live in the card it opens.
 */
export const StatusComposition: React.FC<IProps> = ({ taskStatus, counts, variant }) => {
  const buckets = getSubtaskStatusBreakdown(counts, taskStatus)

  if (!isTerminalTaskStatus(taskStatus) || buckets.length === 0) return null

  const tokens = STATUS_COMPOSITION_TOKENS[variant]
  const statusColor = TASK_STATUS_CONFIG[taskStatus].bar

  const trigger = (
    <Flex
      role="button"
      tabIndex={0}
      direction="column"
      align="center"
      borderRadius="4px"
      // Transparent padding pulled back by a negative margin: a comfortable tap target on touch
      // without making the row any taller.
      p={variant === 'mobile' ? '6px 0' : '0'}
      m={variant === 'mobile' ? '-6px 0' : '0'}
      style={{ gap: tokens.blockGap, cursor: 'pointer', outline: 'none' }}
      css={css`
        /* No underline to thicken anymore — the whole block dims slightly instead. */
        &:hover .composition-caption,
        &:focus-visible .composition-caption {
          opacity: 0.75;
        }
        &:focus {
          outline: none;
        }
        &:focus-visible {
          outline: 2px solid ${withAlpha(statusColor, 0.5)};
          outline-offset: 2px;
        }
      `}
    >
      <Flex className="composition-caption" align="center" style={{ gap: '4px', whiteSpace: 'nowrap' }}>
        {/* The caption carries the status colour — it is the status being explained. */}
        <Text fontSize={CAPTION_FONT_SIZE} fontWeight="700" lineHeight="1.2" color={statusColor}>
          <LocaleText text={SUMMARY_LITERAL} />
        </Text>
      </Flex>
      <CompositionBar buckets={buckets} total={counts.total} width={tokens.barWidth} height={tokens.barHeight} />
    </Flex>
  )

  return (
    // The whole row is clickable (it opens the task) — neither the trigger nor anything inside the
    // popover may bubble up to it. React portals still bubble through the React tree, so this one
    // wrapper covers both.
    <Box
      onClick={(event: React.MouseEvent) => event.stopPropagation()}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') event.stopPropagation()
      }}
    >
      <HoverPopover
        openOn="click"
        usePortal
        trigger={trigger}
        placement="bottom"
        width="296px"
        minWidth="296px"
        maxWidth="296px"
        bodyMaxHeight="none"
        contentProps={{
          borderRadius: '12px',
          border: '1px solid #EAECF0',
          boxShadow: '0 12px 32px rgba(16,24,40,.16)',
          _focus: { outline: 'none' },
        }}
      >
        <Box p="14px">
          <Flex align="baseline" justify="space-between" mb="10px" style={{ gap: '12px' }}>
            <Text fontSize="13px" fontWeight="700" color="#0F172A">
              <LocaleText text={TASK_STATUS_LOCALE_KEY[taskStatus]} />
            </Text>
            <Text
              fontSize="12px"
              color="#98A2B3"
              flexShrink={0}
              style={{ fontVariantNumeric: 'tabular-nums', unicodeBidi: 'isolate' }}
            >
              {counts.total}
            </Text>
          </Flex>

          <Box mb="12px">
            <CompositionBar buckets={buckets} total={counts.total} width={POPOVER_BAR_WIDTH} height={6} />
          </Box>

          {buckets.map(({ key, count }) => (
            <Flex key={key} align="center" mb="8px" style={{ gap: '10px' }}>
              <Flex w="14px" justify="center" flexShrink={0}>
                <Box w="8px" h="8px" borderRadius="50%" bg={TASK_STATUS_CONFIG[key].bar} />
              </Flex>
              <Text fontSize="13px" color="#344054" flex={1} minW={0}>
                <LocaleText text={SUBTASK_STATUS_LOCALE_KEY[key]} />
              </Text>
              <Text
                fontSize="13px"
                fontWeight="700"
                color="#0F172A"
                flexShrink={0}
                style={{ fontVariantNumeric: 'tabular-nums', unicodeBidi: 'isolate' }}
              >
                {count}
              </Text>
            </Flex>
          ))}
        </Box>
      </HoverPopover>
    </Box>
  )
}
